import React, {useState, useEffect} from 'react';
import { Alert, Platform, ScrollView, Share, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native'

import tw from '../../../styles/tailwind';
import Text from '../../../components/Text';
import Icon from '../../../components/IcomoonIcon';
import BottomSheet from '../../../components/BottomSheet';
import Input from '../../../components/Forms/Input';
import { useForm } from 'react-hook-form';
import IconButton from '../../../components/IconButton';
import useFormSubmit from '../../../hooks/useFormSubmit';
import api from '../../../utils/api';
import Select from '../../../components/Forms/Select';
import { useToast } from 'react-native-toast-notifications';


function NewResidentForm({ onResidentCreated, communityId, unitId, visible  }){
  const { control, handleSubmit, reset, setValue } = useForm();

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: onResidentCreated,
    onError: (error) => { },
    successToastMessage: "Residente creado",
    endpoint: `/communities/${communityId}/units/${unitId}/residents`,
  })

  function _submitForm(data) {
    const _data = { ...data }
    // _data.type = "resident"
    submitForm(_data)
  }


  useEffect(() => {
    reset({})
  }, [visible]);

  return (
    <>
      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`pt-5 px-5`} keyboardShouldPersistTaps='handled'>
        <Text maxFontSizeMultiplier={1.25} style={tw`text-lg font-500 text-secondary mb-4`}>Agregar residente</Text>

        <View style={tw`mb-0`}>
          <Text maxFontSizeMultiplier={1.25} style={tw`text-gray-500`}>Nombre</Text>
          <Input control={control} name='name' rules={{ required: true }} />
        </View>

        {/* Tipo */}
        <View style={tw`mb-0`}>
          <Text style={tw`text-gray-500`}>Tipo de residente</Text>

          <Select
            control={control}
            name='type'
            placeholder={{ value: "", label: "Seleccionar tipo" }}
            items={[
              // { value: "owner", label: "Propietario" },
              { value: "resident", label: "Residente" },
              { value: "tenant", label: "Arrendatario" },
            ]}
            rules={{ required: true }}
          />
        </View>
      </ScrollView>

      <IconButton
        disabled={isSubmitting}
        title={isSubmitting ? "Guardando" : "Guardar residente"}
        style={tw.style(`rounded-b-0 rounded-3xl py-3`)}
        onPress={handleSubmit(_submitForm)}
      />
    </>
  )
}


function ResidentDetail({ resident, communityId, unitId, onDeleted }){
  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [invitationSent, setInvitationSent] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setInvitationSent(false)
  }, [resident]);

  const deleteResident = () => {
    setIsDeleting(true);
    api.delete(`/communities/${communityId}/units/${unitId}/residents/${resident.uuid}`)
      .then(() => {
        toast.show("Residente eliminado");
        onDeleted?.();
      })
      .catch((error) => {
        alert(error.response?.data?.message || "Error al eliminar");
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const confirmDeletion = () => {
    if (Platform.OS === "web") {
      if (confirm(`¿Eliminar a ${resident.name}?\nEsta acción no podrá deshacerse`)) {
        deleteResident();
      }
    } else {
      Alert.alert(
        '¿Eliminar residente?',
        `${resident.name} será eliminado. Esta acción no podrá deshacerse`,
        [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Eliminar', style: 'destructive', onPress: deleteResident },
        ]
      );
    }
  };

  // TODO: ABSTRACT_USER_INVITATION
  function createInvitation() {
    setCreatingInvitation(true)

    api.post(`/residents/${resident?.uuid}/invitation`)
      .then(({ data }) => {
        setCreatingInvitation(false)

        const shareMsg = data.message
        shareInvitation(shareMsg)
      })
      .catch((response) => {
        setCreatingInvitation(false)
        console.log("error", response);
      })
  }

  async function shareInvitation(message: string) {
    try {
      const result = await Share.share({
        message,
      });
      
      if (result.action === Share.sharedAction) {
        if (result.activityType) {
          // shared with activity type of result.activityType
        } else {
          // shared
        }
        setInvitationSent(true)
      } else if (result.action === Share.dismissedAction) {
        // dismissed
      }
    } catch (error: any) {
      Alert.alert(error.message);
    }
  }

  return (
    <View style={tw`flex-1 px-5 py-5`}>
      <Text maxFontSizeMultiplier={1.25} style={tw`text-secondary text-lg font-500 mb-5`}>{resident.name}</Text>

      {
        !resident.user?.is_registered ?
          <View>
            <Text maxFontSizeMultiplier={1.25} style={tw`text-gray-400 mb-5`}>El residente ha sido creado, envíale un código de invitación para que pueda acceder a la app</Text>

            <IconButton
              title={!invitationSent ? 'Enviar invitación' : 'Invitación enviada'}
              style={tw.style(`py-4`, invitationSent && `bg-white border-primary border`)}
              textColor={invitationSent ? 'primary' : undefined }
              icon={invitationSent ? null : undefined}
              onPress={createInvitation}
              disabled={invitationSent}
            />
          </View>
        :
          <View>
            <Text maxFontSizeMultiplier={1.25} style={tw`text-gray-400 mb-5`}>Este residente ya se ha registrado a la app</Text>
          </View>
      }

      <TouchableOpacity
        style={tw`mt-auto py-4 items-center`}
        onPress={confirmDeletion}
        disabled={isDeleting}
      >
        <Text style={tw`text-red-500`}>
          {isDeleting ? 'Eliminando...' : 'Eliminar residente'}
        </Text>
      </TouchableOpacity>
    </View>
  )
}


export default function NewResidentModal({
    onRequestClose,
    visible,
    communityId,
    unitId,
    onResidentCreated,
    onResidentDeleted,
    residents
}) {
  const [selectedResident, setSelectedResident] = useState();

  const hasResidents = residents.length > 0

  const height = (560 + residents.length * 80)


  useEffect(() => {
    setSelectedResident(null)
  }, [visible]);

  return (
    <BottomSheet onRequestClose={onRequestClose} showCloseButton visible={visible} height={height}>
      <View style={tw`bg-secondary pt-6 pb-4`}>
        <View style={tw`pl-10 mb-4`}>
          <Text maxFontSizeMultiplier={1.25} style={tw`text-lg font-500 text-white `}>Residentes</Text>

          <TouchableOpacity style={tw`absolute right-7`} onPress={onRequestClose}>
            <Icon name='close' color={tw.color('white')} size={26} />
          </TouchableOpacity>
        </View>

        {
          hasResidents &&
          <View style={tw`bg-gray-100 mx-3 border border-gray-200 rounded-xl`}>
            {
              residents.map((resident, i) =>
                <TouchableOpacity
                  onPress={() => setSelectedResident(resident)}
                  key={resident.uuid}
                  style={tw.style(`row border-b border-gray-200 px-5 py-3`, (i + 1) == residents.length && `border-b-0`)}
                >
                  <Text maxFontSizeMultiplier={1.25} style={tw`text-base text-gray-600 flex-1`}>{resident.name}</Text>

                  <Icon name='chevron-right' color={tw.color('secondary')} size={13} />
                </TouchableOpacity>
              )
            }
          </View>
        }
      </View>
      

      {
        selectedResident ?
          <ResidentDetail
            resident={selectedResident}
            communityId={communityId}
            unitId={unitId}
            onDeleted={() => {
              onResidentDeleted?.();
              setSelectedResident(null);
            }}
          />
        :
          <NewResidentForm
            communityId={communityId}
            onResidentCreated={resident => {
              onResidentCreated?.(resident)
              setSelectedResident(resident)
            }}
            unitId={unitId}
            visible={visible}
          />
      }
    </BottomSheet>
  );
}
